import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import {
  setVideoUploadProgress,
  setAudioUploadProgress,
  setVideoUploading,
  setAudioUploading,
  setVideoUploadComplete,
  setAudioUploadComplete,
  setVideoUploadError,
  setAudioUploadError,
  resetVideoUploadState,
  resetAudioUploadState,
  setDocumentUploadError,
  setDocumentUploadProgress,
  setDocumentUploading
} from '../redux/slices/modalSlice';
import { apiCall } from '../utils';
import { store } from '../redux/store';
import { mediaApiSlice } from '../redux/slices/mediaApi';

export const uploadCancelTokens = {};
export const handleUpload = async ({
  file,
  onProgress,
  onSuccess,
  onError,
  isVideo,
  dispatch,
  setMediaId,
  setDuration,
  openNotification,
  setAudioLink,
  setBulkData,
  cancelToken
}) => {
  if (isVideo) {
    try {
      dispatch(setVideoUploading(true));
      dispatch(setVideoUploadError(false));

      const initResponse = await store.dispatch(
        mediaApiSlice.endpoints.initializeUpload.initiate({
          fileName: file.name,
          fileSize: file.size,
          uuid: uuidv4(),
          mimeType: file.type
        })
      );

      const { uploadId, chunkSize } = initResponse?.data?.data;

      const chunks = [];
      let offset = 0;
      while (offset < file.size) {
        const chunk = file.slice(offset, offset + chunkSize);
        chunks.push(chunk);
        offset += chunkSize;
      }

      const uploadedChunks = new Set();
      const totalChunks = chunks.length;

      for (let index = 0; index < totalChunks; index++) {
        try {
          const formData = new FormData();
          formData.append('chunk', chunks[index]);
          formData.append('uuid', uploadId);
          formData.append('chunkIndex', index);
          formData.append('totalChunks', totalChunks);

          const chunkResponse = await apiCall({
            serviceType: '/media-service',
            endpoint: '/upload-chunck',
            method: 'POST',
            body: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const percent = Math.round(((uploadedChunks.size + 1) * 100) / totalChunks);
              dispatch(setVideoUploadProgress(percent));
              onProgress({ percent });
            },
            cancelToken
          });

          uploadedChunks.add(index);

          if (uploadedChunks.size === totalChunks) {
            const { id } = chunkResponse.data.data;
            file.id = id;

            const meta = {
              id,
              fileName: file.name,
              author: null,
              title: null,
              description: 'description',
              thumbnailUrl: '',
              language: 'en',
              accessPermissions: ['view', 'download'],
              relatedDocuments: [],
              videoCategory: null,
              metaTags: null,
              visibility: null,
              theme: null,
              isMetaDataComplete: false
            };
            setMediaId(id);

            setBulkData((prevData) => ({ ...prevData, [id]: meta }));

            onSuccess(null, file);
            dispatch(setVideoUploadComplete(true));
          }
        } catch (chunkError) {
          if (axios.isCancel(chunkError)) {
            console.error(`Chunk upload canceled for file: ${file.name}`);
            throw chunkError;
          } else {
            throw new Error(`Failed to upload chunk ${index + 1}: ${chunkError.message}`);
          }
        }
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        openNotification({
          message: 'Video upload canceled',
          type: 'info',
          title: 'Upload Canceled'
        });
      } else {
        openNotification({
          message: 'Video upload failed',
          type: 'error',
          title: 'Error'
        });
      }
      onError(error);
      dispatch(setVideoUploadError(true));
    } finally {
      dispatch(setVideoUploading(false));
    }
  } else {
    const cancelTokenSource = axios.CancelToken.source();
    uploadCancelTokens[file.uid] = cancelTokenSource;

    const formData = new FormData();
    formData.append(isVideo ? 'video' : 'audio', file);
    formData.append('title', file.name);

    try {
      const response = await apiCall({
        serviceType: '/media-service',
        endpoint: `/upload-${isVideo ? 'video' : 'audio'}`,
        method: 'POST',
        body: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          dispatch(isVideo ? setVideoUploadProgress(percent) : setAudioUploadProgress(percent));
          onProgress({ percent });
        },
        cancelToken: cancelTokenSource.token
      });

      openNotification({
        message: `${isVideo ? 'Video' : 'Audio'} uploaded successfully. ${isVideo ? 'Complete the Video Details' : 'Complete the Audio Details'
          }`,
        type: 'success',
        title: 'Success'
      });

      onSuccess(null, file);

      dispatch(setAudioUploadComplete(true));
      const { audioId, audioDurationInMinutes, audioLink } = response.data.data;
      setMediaId(audioId);
      setDuration(audioDurationInMinutes);
      setAudioLink(audioLink);
    } catch (error) {
      if (axios.isCancel(error)) {
        openNotification({
          message: 'Audio upload canceled',
          type: 'info',
          title: 'Upload Canceled'
        });
      } else {
        openNotification({
          message: 'Audio upload failed',
          type: 'error',
          title: 'Error'
        });
        onError(error);
        dispatch(setAudioUploadError(true));
      }
    } finally {
      dispatch(setAudioUploading(false));
      delete uploadCancelTokens[file.uid];
    }
  }
};

export const handleDocumentUpload = async ({
  file,
  onProgress,
  onSuccess,
  onError,
  openNotification,
  dispatch,
  isVideo,
  selectedMediaUid,
  setBulkData
}) => {
  const formData = new FormData();
  formData.append('document', file);
  formData.append('title', file.name);
  formData.append('document_type', isVideo ? 'video' : 'audio');
  formData.append('visibility', '[3]');

  try {
    dispatch(setDocumentUploading(true));

    const response = await apiCall({
      serviceType: '/media-service',
      endpoint: '/upload-document',
      method: 'POST',
      body: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        dispatch(setDocumentUploadProgress({ uid: file.uid, progress: percent }));
        if (onProgress) onProgress({ percent });
      }
    });

    openNotification({
      message: `${file.name} uploaded successfully`,
      type: 'success',
      title: 'Success'
    });

    onSuccess(null, file);
    const { id, title, sharableLink, lastModifiedDate, url } = response.data.data;
    const fileUUID = file.uid;

    setBulkData((prevData) => ({
      ...prevData,
      [selectedMediaUid]: {
        ...prevData[selectedMediaUid],
        relatedDocuments: [
          ...(prevData[selectedMediaUid]?.relatedDocuments || []),
          {
            fileUUID,
            id,
            name: title,
            status: 'done',
            sharableLink,
            lastModifiedDate,
            url
          }
        ]
      }
    }));

    dispatch(setDocumentUploadProgress({ uid: file.uid, progress: 100 }));
  } catch (error) {
    dispatch(setDocumentUploadError(file.uid));
    onError(error);
  } finally {
    dispatch(setDocumentUploading(false));
  }
};

export const handleFileChange = (info, setFileList) => {
  setFileList(info.fileList);
};

export const handleVisibilityChange = (selectedValues, selectedMediaTypes) => {
  let newDisabledItems = [];
  let effectiveValues = [...selectedValues];
  const mediaTypesArray = Array.isArray(selectedMediaTypes) ? selectedMediaTypes : [];

  if (mediaTypesArray.includes(3) || mediaTypesArray.includes(5)) {
    console.log('here 3 and 5');

    newDisabledItems = [3];
    effectiveValues = effectiveValues.filter((value) => value !== 3);
  }
  // if visibility section public is selected, bookroad and other ecclesia should not be selected please remove this
  else if (effectiveValues.includes(3)) {
    console.log('here 3');
    [1, 2].forEach((item) => {
      if (!effectiveValues.includes(item)) {
        effectiveValues.push(item);
      }
    });
    newDisabledItems = [];
  } else if (effectiveValues.some((v) => [1, 2].includes(v))) {
    console.log('here 1 and 2');
    newDisabledItems = [3];
  } else {
    console.log('else');
    newDisabledItems = [];
  }

  return { effectiveValues, newDisabledItems };
};

export const handleVisibilityChangeCustom = (
  selectedValues,
  setDisabledItems,
  form,
  selectedMediaTypes
) => {
  console.log('selectedValues:', selectedValues);

  let newDisabledItems = [];
  let effectiveValues = [...selectedValues];
  const mediaTypes = Array.isArray(selectedMediaTypes) ? selectedMediaTypes : [];

  if (mediaTypes.includes(3) || mediaTypes.includes(5)) {
    newDisabledItems = [3];
    effectiveValues = selectedValues.filter((value) => value !== 3);
  } else if (selectedValues.includes(3)) {
    [2, 1].forEach((item) => {
      if (!effectiveValues.includes(item)) {
        effectiveValues.push(item);
      }
    });
    newDisabledItems = [2, 1];
  } else if (selectedValues.includes(2) || selectedValues.includes(1)) {
    newDisabledItems = [3];
  } else {
    newDisabledItems = [];
  }

  setDisabledItems(newDisabledItems);
  form.setFieldsValue({ visibility: effectiveValues });
};

export const handleSubmit = async (
  form,
  mediaId,
  documentIds,
  isVideo,
  duration,
  onClose,
  resetState,
  setLoading,
  openNotification,
  tags,
  setFileList,
  audioLink,
  dispatch
) => {
  try {
    setLoading(true);

    const state = store.getState().modal;
    if (
      state.isDocumentUploading ||
      Object.values(state.documentUploadProgress).some((p) => p < 100)
    ) {
      openNotification({
        message: 'Document upload in progress. Please wait for it to complete.',
        type: 'warning',
        title: 'Warning'
      });
      setLoading(false);
      return;
    }

    if (state.documentUploadErrors.length > 0) {
      openNotification({
        message: 'Some documents failed to upload. Please try again.',
        type: 'error',
        title: 'Error'
      });
      setLoading(false);
      return;
    }

    const values = await form.validateFields();
    const payload = isVideo
      ? {
        id: mediaId,
        author: values.speaker,
        title: values.title,
        description: values.description || 'Default description',
        thumbnailUrl: '',
        language: 'en',
        theme: values.theme,
        accessPermissions: ['view', 'download'],
        relatedDocuments: documentIds,
        videoCategory: values.mediaType || [],
        metaTags: tags || [],
        visibility: values.visibility || []
      }
      : {
        audioId: mediaId,
        audioDurationInMinutes: duration,
        audioTitle: values.title,
        audioLink: audioLink,
        theme: values.theme,
        speaker: values.speaker,
        audioTypeIDs: values.mediaType || [],
        visibilityID: values.visibility || []
      };

    const endpoint = isVideo ? '/submit-video' : '/upload-audio';

    await apiCall({
      serviceType: '/media-service',
      endpoint,
      method: 'POST',
      body: payload,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    openNotification({
      message: `${isVideo ? 'Video' : 'Audio'} submitted successfully`,
      type: 'success',
      title: 'Success'
    });

    onClose();
    form.resetFields();
    resetState();
    dispatch(mediaApiSlice.util.invalidateTags([isVideo ? 'Videos' : 'Audios']));
    setFileList([]);
  } catch (error) {
    console.error('Submission Failed:', error);
    openNotification({
      message: 'Media submission failed. Please try again.',
      type: 'error',
      title: 'Error'
    });
  } finally {
    setLoading(false);
  }
};

export const handleVideoSubmit = async (
  data,
  openNotification,
  isVideo,
  handleInputChange,
  setBulkData
) => {
  delete data.fileName;
  delete data.isMetaDataComplete;

  try {
    await apiCall({
      serviceType: '/media-service',
      endpoint: '/submit-video',
      method: 'POST',
      body: data,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    handleInputChange('isMetaDataComplete', true);

    setBulkData((prevData) => {
      const newData = { ...prevData };
      delete newData[data.id];
      return newData;
    });

    openNotification({
      message: `${isVideo ? 'Video' : 'Audio'} submitted successfully`,
      type: 'success',
      title: 'Success'
    });
  } catch (error) {
    console.error('Submission Failed:', error);
    openNotification({
      message: 'Media submission failed. Please try again.',
      type: 'error',
      title: 'Error'
    });
  }
};

export const resetFileList = (setFileList, isVideo, dispatch) => {
  setFileList([]);
  if (isVideo) {
    dispatch(resetVideoUploadState());
  } else {
    dispatch(resetAudioUploadState());
  }
};
